import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { SESSIONS } from '../config/builtSessions';
import './AdminLeads.css';
import './AdminBuilt.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/built`;
const TOKEN_KEY = 'built_admin_token';

const Login = ({ onToken }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onToken(data.token);
    } catch (err) {
      setError('wrong password');
    }
  };
  return (
    <div className="al-page ab-login">
      <form onSubmit={submit} className="ab-login-card" data-testid="ab-login-form">
        <h1 className="al-h">built · sign-ups</h1>
        <input type="password" placeholder="password" value={password} onChange={(e) => setPassword(e.target.value)} className="al-search" data-testid="ab-password" />
        {error && <p className="al-error" data-testid="ab-login-error">{error}</p>}
        <button type="submit" className="al-btn ab-btn-ink" data-testid="ab-login-submit">sign in</button>
      </form>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="ab-stat"><span className="ab-stat-v" data-testid={testid}>{value}</span><span className="ab-stat-l">{label}</span></div>
);

const QUIZ_COLS = ['take', 'career', 'work', 'more_money', 'time_off', 'gift', 'considered', 'know', 'want'];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '');

export default function AdminBuilt() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setData(null); };

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/signups`, {
        headers: { Authorization: `Bearer ${token}` }, params: { session_id: filter },
      });
      setData(d);
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  }, [token, filter]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const onExport = async () => {
    const res = await axios.get(`${API}/admin/export.csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'built-signups.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) return <Login onToken={setToken} />;
  const s = data?.stats;

  return (
    <div className="al-page">
      <main className="al-main">
        <header className="al-header">
          <h1 className="al-h">built · sign-ups</h1>
          <div className="al-header-actions">
            <button type="button" className="al-btn al-btn-outline" onClick={onExport} data-testid="ab-export">export CSV</button>
            <button type="button" className="al-btn al-btn-ghost" onClick={logout} data-testid="ab-logout">sign out</button>
          </div>
        </header>

        {s && (
          <div className="ab-stats" data-testid="ab-stats">
            <Stat label="/built views" value={s.built_views} testid="ab-stat-built-views" />
            <Stat label="sign-ups" value={s.signups} testid="ab-stat-signups" />
            <Stat label="sign-up rate" value={`${s.signup_rate}%`} testid="ab-stat-rate" />
            <Stat label="recording only" value={s.recording_only} testid="ab-stat-recording" />
            <Stat label="/opportunity views" value={s.opportunity_views} testid="ab-stat-opp-views" />
            <Stat label="quizzes completed" value={s.quizzes_completed} testid="ab-stat-quizzes" />
            {s.per_session.map((p) => (
              <Stat key={p.session_id} label={p.label} value={p.count} testid={`ab-stat-session-${p.session_id}`} />
            ))}
          </div>
        )}

        <div className="al-controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="al-filter" data-testid="ab-filter">
            <option value="all">all sessions</option>
            {SESSIONS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            <option value="recording">recording only</option>
            <option value="quiz_only">quiz only (no sign-up)</option>
          </select>
        </div>

        <div className="al-table-wrap">
          <table className="al-table" data-testid="ab-table">
            <thead>
              <tr><th>submitted</th><th>first name</th><th>email</th><th>session</th><th>utm</th>{QUIZ_COLS.map((c) => <th key={c}>{c}</th>)}<th>quiz_completed_at</th><th>klaviyo error</th></tr>
            </thead>
            <tbody>
              {(data?.signups || []).map((r) => (
                <tr key={r.email} data-testid="ab-row">
                  <td>{fmt(r.submitted_at)}</td>
                  <td>{r.first_name}</td>
                  <td>{r.email}</td>
                  <td>{r.session_label}</td>
                  <td>{[r.utm_source, r.utm_medium, r.utm_campaign, r.utm_content].filter(Boolean).join(' · ')}</td>
                  {QUIZ_COLS.map((c) => <td key={c} data-testid={`ab-cell-${c}`}>{Array.isArray(r[c]) ? r[c].join(', ') : r[c]}</td>)}
                  <td>{fmt(r.quiz_completed_at)}</td>
                  <td className="ab-err">{r.klaviyo_error}</td>
                </tr>
              ))}
              {data && data.signups.length === 0 && <tr><td colSpan={16}>no sign-ups yet</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
